import type { ReviewComment, PendingCommentSelection } from "@/components/ai/review/types";
import { GitPullRequest } from "lucide-react";
import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button";
import { Text } from "@/components/podkit/typography/Text";
import { useEnvironment, useStartEnvironment } from "@/queries/environment-queries";
import { FileChangeList, type FileChangeListRef } from "@/routes/environments/code-changes/FileChangeList";
import { showStart } from "@/routes/environments/phase";
import { EnvironmentGitStatus } from "gitpod-next-api/gitpod/v1/environment_pb";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";

export type CodeChangesPanelRef = {
    scrollToFile: (filePath: string) => void;
    openFile: (filePath: string) => void;
};

export type CodeChangesPanelProps = {
    environmentId: string;
    enableReviewComments?: boolean;
    comments?: ReviewComment[];
    pendingSelection?: PendingCommentSelection | null;
    onLineSelectionForComment?: (selection: PendingCommentSelection) => void;
    onAddComment?: (filePath: string, startLine: number, endLine: number, content: string) => void;
    onCancelComment?: () => void;
    onStartReview?: () => void;
    onCreatePullRequest?: () => void;
    isReviewLoading?: boolean;
    isCreatePRLoading?: boolean;
    isCreatePRDisabled?: boolean;
};

export const CodeChangesPanel = forwardRef<CodeChangesPanelRef, CodeChangesPanelProps>(
    (
        {
            environmentId,
            enableReviewComments = false,
            comments = [],
            pendingSelection,
            onLineSelectionForComment,
            onAddComment,
            onCancelComment,
            onStartReview,
            onCreatePullRequest,
            isReviewLoading = false,
            isCreatePRLoading = false,
            isCreatePRDisabled = false,
        },
        ref,
    ) => {
        const { data: environment } = useEnvironment(environmentId);
        const { mutate: startEnvironment, isPending: isStarting } = useStartEnvironment();
        const [openedFiles, setOpenedFiles] = useState<string[]>([]);
        const fileChangeListRef = useRef<FileChangeListRef>(null);

        const gitStatus = environment?.status?.content?.git;
        const hasChanges = Boolean(gitStatus?.changedFiles.length);
        const shouldShowStartEnv = environment ? showStart(environment) : false;

        useImperativeHandle(
            ref,
            () => ({
                scrollToFile: (filePath: string) => {
                    fileChangeListRef.current?.scrollToFile(filePath);
                },
                openFile: (filePath: string) => {
                    // Open the file in the accordion
                    if (!openedFiles.includes(filePath)) {
                        setOpenedFiles((prev) => [...prev, filePath]);
                    }
                    // Then scroll to it
                    fileChangeListRef.current?.scrollToFile(filePath);
                },
            }),
            [openedFiles],
        );

        const handleStartEnvironment = () => {
            if (environment) {
                startEnvironment({ environmentId: environment.id });
            }
        };

        if (!environment) {
            return null;
        }

        return (
            <div>
                <div className="flex w-full items-center justify-between py-2.5">
                    <span className="mr-2 select-none text-start text-md font-medium">Code Changes</span>
                    {enableReviewComments && hasChanges && (
                        <div className="flex items-center gap-2">
                            {onStartReview && (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={onStartReview}
                                    disabled={isReviewLoading}
                                    data-tracking-id="start-review-code-changes"
                                >
                                    {isReviewLoading ? "Reviewing..." : "Review"}
                                </Button>
                            )}
                            {onCreatePullRequest && (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={onCreatePullRequest}
                                    disabled={isCreatePRLoading || isCreatePRDisabled}
                                    data-tracking-id="create-pull-request"
                                >
                                    <GitPullRequest size={16} className="shrink-0" />
                                    <span>{isCreatePRLoading ? "Creating..." : "Create PR"}</span>
                                </Button>
                            )}
                        </div>
                    )}
                </div>
                {shouldShowStartEnv && hasChanges && (
                    <Banner
                        className="mb-2"
                        variant="info"
                        text="Start the environment to view code changes."
                        action={{
                            text: "Start environment",
                            onClick: handleStartEnvironment,
                            loading: isStarting,
                            "data-tracking-id": "start-environment-code-changes-panel",
                        }}
                    />
                )}
                {hasChanges ? (
                    <FileChangeList
                        ref={fileChangeListRef}
                        environment={environment}
                        gitStatus={new EnvironmentGitStatus(gitStatus)}
                        openedFiles={openedFiles}
                        onOpenedFilesChange={setOpenedFiles}
                        enableReviewComments={enableReviewComments}
                        comments={comments}
                        pendingCommentSelection={pendingSelection}
                        onLineSelectionForComment={onLineSelectionForComment}
                        onAddComment={onAddComment}
                        onCancelComment={onCancelComment}
                    />
                ) : (
                    <Text className="text-sm text-content-secondary">No uncommitted changes</Text>
                )}
            </div>
        );
    },
);
CodeChangesPanel.displayName = "CodeChangesPanel";
